import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { requireAuthedUser } from "helpers/auth";
import buildRoute from "components/buildRoute";
import PageHeader from "components/PageHeader";
import FileUploadForm from "../components/FileUploadForm";
import FileStorageSchemasList from "../components/FileStorageSchemasList";
import * as fileUploaderApi from "../fileUploaderApi";
import { Box, Button, Card, CardContent, Alert, Tab, Tabs } from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import getCurrentUser from "hooks/getCurrentUser";
import { hasWriteAccess } from "utils/userPermissions";
import { trackEvent, trackEngagementActivity } from "../../../configureMixpanel";

const FileUploaderPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [noTemplates, setNoTemplates] = useState(false);

  const loggedInUser = getCurrentUser();
  const userActiveRoles = loggedInUser?.roles?.filter(
    (role) => role.partner_gcp_project_id === projectId
  ) ?? [];
  const userCanViewFiles = hasWriteAccess(userActiveRoles);

  const handleTabChange = (event, newValue) => {
    if (!userCanViewFiles && newValue === 1) return;
    setActiveTab(newValue);
    setUploadSuccess(false);
    setError("");
    trackEvent("file_uploader_tab_changed", { tab: newValue === 1 ? "view_files" : "upload" });
  };

  const handleUploadSuccess = useCallback(() => {
    setUploadSuccess(true);
    if (userCanViewFiles) {
      setActiveTab(1);
    }
    setTimeout(() => setUploadSuccess(false), 5000);
  }, [userCanViewFiles]);

  const handleError = useCallback((errorMsg) => {
    setError(errorMsg);
  }, []);

  useEffect(() => {
    trackEngagementActivity("file_uploader_opened");
  }, []);

  useEffect(() => {
    const checkTemplates = async () => {
      try {
        const templates = await fileUploaderApi.fetchFileTemplates(projectId);
        setNoTemplates(!templates || templates.length === 0);
      } catch {
        setNoTemplates(false);
      }
    };
    checkTemplates();
  }, [projectId]);

  return (
    <Box sx={{ pb: 3 }}>
      <PageHeader
        title="File Uploader"
        action={
          userCanViewFiles ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AdminPanelSettingsIcon />}
              onClick={() => navigate(`/projects/${projectId}/file-uploader/admin`)}
            >
              Template Admin
            </Button>
          ) : null
        }
      />

      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ mb: 2, alignItems: "flex-start", "& .MuiAlert-action": { pt: "2px" } }}
        >
          {error}
        </Alert>
      )}
      {uploadSuccess && (
        <Alert
          severity="success"
          onClose={() => setUploadSuccess(false)}
          sx={{ mb: 2 }}
        >
          File uploaded successfully
        </Alert>
      )}
      {noTemplates && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {userCanViewFiles ? (
            <>
              No file templates found. Create one on the{" "}
              <a href={`/projects/${projectId}/file-uploader/admin`}>File Uploader admin page</a>.
            </>
          ) : (
            "No file templates found. Contact your admin to set up file templates."
          )}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab
              label="Upload Files"
              icon={<CloudUploadIcon />}
              iconPosition="start"
              disabled={noTemplates}
            />
            {userCanViewFiles && <Tab label="View Files" />}
          </Tabs>
        </CardContent>
      </Card>

      <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
        <FileUploadForm
          projectId={projectId}
          onSuccess={handleUploadSuccess}
          onError={handleError}
          api={fileUploaderApi}
          disabled={noTemplates}
        />
      </Box>

      {userCanViewFiles && (
        <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
          <FileStorageSchemasList
            projectId={projectId}
            api={fileUploaderApi}
            onError={handleError}
          />
        </Box>
      )}
    </Box>
  );
};

const routeArgs = {
  element: <FileUploaderPage />,
  loader: async (context) => {
    const user = await requireAuthedUser(context);
    return { user };
  },
};

export default buildRoute(routeArgs);
